import { zodResolver } from '@hookform/resolvers/zod'
import Button from 'components/Button'
import FormRow from 'components/Form/FormRow'
import InputField from 'components/Form/Inputs/InputField'
import SubmitButton from 'components/Form/SubmitButton'
import ModalBody from 'components/Modal/ModalBody'
import ModalFooter from 'components/Modal/ModalFooter'
import UrlModal from 'components/Modal/UrlModal'
import { notificationsQueryKey, useNotificationsQuery } from 'components/Notifications/queries'
import { ShareRequest } from 'components/Notifications/types'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { queryClient } from 'utils/queryClient'
import * as z from 'zod'
import { recipesQueryKey, useAcceptSharedRecipeMutation, useDismissSharedRecipeMutation } from '../queries'

type Inputs = {
    name: string
}

const schema = z.object({
    name: z.string().min(1, 'Required')
})

function AcceptSharedRecipeForm() {
    const navigate = useNavigate()
    const { shareRequestId } = useParams()

    const { data: notificationsData } = useNotificationsQuery()
    const { mutateAsync: acceptSharedRecipe } = useAcceptSharedRecipeMutation()
    const { mutate: dismissSharedRecipe, isLoading: isDismissSharedRecipeLoading } = useDismissSharedRecipeMutation()

    const shareRequests = notificationsData?.notifications.filter(({ type }) => type === 'share_request')?.map(({ meta }) => meta) as
        | ShareRequest[]
        | undefined
    const shareRequest = shareRequests?.find(({ share_request_id }) => share_request_id === Number(shareRequestId))

    const methods = useForm<Inputs>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            name: ''
        },
        values: {
            name: shareRequest?.recipe_name || ''
        }
    })

    const {
        handleSubmit,
        formState: { isValid, isSubmitting }
    } = methods

    const onSubmit: SubmitHandler<Inputs> = async ({ name }) => {
        await acceptSharedRecipe(
            { newRecipeName: name, shareRequestId: Number(shareRequestId) },
            {
                onSuccess: (res) => {
                    toast.success(res.message)
                    queryClient.invalidateQueries(notificationsQueryKey())
                    queryClient.invalidateQueries(recipesQueryKey())
                    navigate(`/recipes/edit/${res.data?.new_recipe_id}`)
                }
            }
        )
    }

    const onDismiss = () => {
        dismissSharedRecipe(Number(shareRequestId), {
            onSuccess: (res) => {
                toast.success(res.message)
                queryClient.invalidateQueries(notificationsQueryKey())
                navigate(-1)
            }
        })
    }

    return (
        <div>
            <UrlModal
                onClose={() => navigate('/recipes')}
                title='Accept Recipe'
                desc={`"${shareRequest?.recipe_name || ''}" from ${shareRequest?.owner_name || ''}`}
            >
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <ModalBody>
                            <FormRow>
                                <InputField.HookForm label='Save Recipe As' name='name' />
                            </FormRow>
                        </ModalBody>
                        <ModalFooter
                            buttons={[
                                <Button color='error' onClick={onDismiss} loading={isDismissSharedRecipeLoading}>
                                    Dismiss
                                </Button>,
                                <Button key={1} color='secondary' onClick={() => navigate('/recipes')}>
                                    Back
                                </Button>,
                                <SubmitButton key={2} isSubmitting={isSubmitting} isValid={isValid} isDirty={true} text='Accept' />
                            ]}
                        />
                    </form>
                </FormProvider>
            </UrlModal>
        </div>
    )
}

export default AcceptSharedRecipeForm
